import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import {
  BrainCircuit,
  AlertTriangle,
  TrendingUp,
  ShieldCheck,
  Download,
  Filter,
  Search,
  Zap,
  Droplets,
  Activity,
  Lightbulb,
  Waves,
} from "lucide-react";
import { useSelector } from "react-redux";
import { Badge } from "@/components/ui/badge";

const PredictiveAnalytics = () => {
  const themeMode = useSelector((state) => state.theme.mode);
   const textColorClass = themeMode ? "dark_text-color" : "text-primary";
   const bgColorClass = themeMode ? "bg_light_main" : "bg-white";
   const borderThem = themeMode ? "border_them_2" : "border-primary";
    const tableHedBg = themeMode ? "table_hed_bg" : "tabel_hed_def_bg";

  const summaryMetrics = [
    {
      label: "Open Alerts",
      value: 12,
      icon: <AlertTriangle className="text-white" />,
      color: "bg-red-500",
    },
    {
      label: "Critical Alerts",
      value: 4,
      icon: <AlertTriangle className="text-white" />,
      color: "bg-orange-500",
    },
    {
      label: "High Risk Assets",
      value: 7,
      icon: <TrendingUp className="text-white" />,
      color: "bg-yellow-500",
    },
    {
      label: "Resolved",
      value: 48,
      icon: <ShieldCheck className="text-white" />,
      color: "bg-green-500",
    },
  ];

  // Predictive Analytics Points
  const predictiveModels = [
    {
      model: "Failure Prediction",
      inputs: "Vibration, temp, trips",
      output: "Risk score",
      value: "85%",
      status: "Critical",
    },
    {
      model: "Leak Prediction",
      inputs: "Night flow, pressure",
      output: "Leak probability",
      value: "32%",
      status: "Monitor",
    },
    {
      model: "Demand Forecast",
      inputs: "History, season",
      output: "Demand",
      value: "1,240 m³/h",
      status: "Stable",
    },
  ];

  // Prescriptive Analytics Points
  const prescriptiveActions = [
    {
      condition: "High kWh/m³",
      recommendation: "Inspect pump efficiency",
      threshold: "> 0.4 kWh/m³",
      priority: "High",
    },
    {
      condition: "High pressure + high NRW",
      recommendation: "Reduce FPR",
      threshold: "> 4 bar & > 20% NRW",
      priority: "Critical",
    },
    {
      condition: "High vibration",
      recommendation: "Replace bearing",
      threshold: "> 4.5 mm/s",
      priority: "Critical",
    },
    {
      condition: "Falling water table",
      recommendation: "Reduce abstraction",
      threshold: "< 12m Depth",
      priority: "Medium",
    },
  ];

  return (
    <div className={`w-full ${bgColorClass} ${textColorClass} h-full`}>
      {/* Header */}
      <div
        className={`py-4 md:px-12 px-6 border-b ${borderThem} flex justify-between items-center h-[57px]`}
      >
        <div
          className={`flex ps-3 md:ps-0 text-sm md:text-base ${textColorClass} items-center gap-x-2`}
        >
          <BrainCircuit size={16} />
          <p className="font-semibold  tracking-tight">
            Predictive Analytics
          </p>
        </div>
       
      </div>

      <div className="h-[calc(100vh-57px)] pb-10 overflow-y-auto sidebar_scroll">
        <div className="px-3 md:px-5 lg:px-12 p-6 space-y-6">
          {/* Summary Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {summaryMetrics.map((m, i) => (
              <div
                key={i}
                className={` p-5 rounded-xl border ${borderThem} flex justify-between items-center`}
              >
                <div>
                  <p className="text-slate-500 text-xs font-semibold uppercase tracking-widest mb-1">
                    {m.label}
                  </p>
                  <h2 className="text-xl font-semibold tracking-tighter">
                    {m.value}
                  </h2>
                </div>
                <div className={`${m.color} p-3 rounded-xl shadow-lg`}>
                  {m.icon}
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Predictive Analytics Table */}
            <div className={`lg:col-span-8  rounded-xl border ${borderThem} overflow-hidden`}>
              <div className={`p-3 border-b ${borderThem}`}>
                <h3 className="text-sm font-semibold tracking-tight uppercase ">
                  Predictive Analytics Models
                </h3>
                <p className="text-slate-500 text-xs uppercase font-semibold tracking-widest mt-1">
                  Advanced Forecasting Engine
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className={`${tableHedBg} text-[10px] uppercase font-semibold text-slate-500`}>
                    <tr>
                      <th className="p-4">Model</th>
                      <th className="p-4">Inputs</th>
                      <th className="p-4">Output</th>
                      <th className="p-4 text-center">Current Value</th>
                      <th className="p-4 text-right">Status</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    {predictiveModels.map((row, i) => (
                      <tr
                        key={i}
                        className={`border-t ${borderThem}  transition-colors`}
                      >
                        <td className="p-4 font-semibold text-blue-400">
                          {row.model}
                        </td>
                        <td className="p-4  italic text-xs">
                          {row.inputs}
                        </td>
                        <td className="p-4 font-semibold">
                          {row.output}
                        </td>
                        <td className="p-4 text-center font-mono font-semibold ">
                          {row.value}
                        </td>
                        <td className="p-4 text-right">
                          <Badge
                            className={
                              row.status === "Critical"
                                ? "bg-red-500/10 text-red-500 border-red-500/20"
                                : "bg-emerald-500/10 text-emerald-500 border-emerald-500/20"
                            }
                          >
                            {row.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Model Trend Chart */}
            <div className={`lg:col-span-4  rounded-xl border ${borderThem} p-3  flex flex-col justify-between`}>
              <div>
                <h3 className="font-semibold text-sm mb-4 flex items-center gap-2 uppercase tracking-widest text-slate-400">
                  <TrendingUp size={16} className="text-blue-500" /> Confidence
                  Trend
                </h3>
                <div className="h-48 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={[
                        { v: 45 },
                        { v: 52 },
                        { v: 48 },
                        { v: 61 },
                        { v: 55 },
                        { v: 67 },
                      ]}
                    >
                      <Area
                        type="monotone"
                        dataKey="v"
                        stroke="#3b82f6"
                        fill="#3b82f6"
                        fillOpacity={0.1}
                        strokeWidth={3}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="bg-blue-500/5 p-4 rounded-lg border-l-4 border-blue-500 mt-4">
                <p className="text-[10px] font-semibold text-blue-500 uppercase tracking-widest mb-1 underline">
                  Demand Analysis
                </p>
                <p className="text-xs opacity-70">
                  Current system demand is trending 12% higher than seasonal
                  average. Pump abstraction levels may require adjustment.
                </p>
              </div>
            </div>
          </div>

          {/* Prescriptive Analytics Table */}
          <div className={`rounded-xl border ${borderThem} overflow-hidden`}>
            <div className={`p-3 border-b ${borderThem} flex justify-between items-center ${tableHedBg}`}>
              <div>
                <h3 className="text-sm font-semibold tracking-tight uppercase italic flex items-center gap-2">
                  <Lightbulb size={18} className="text-yellow-500" />{" "}
                  Prescriptive Analytics
                </h3>
                <p className="text-slate-500 text-[10px] font-semibold tracking-widest mt-1 uppercase underline decoration-blue-500 underline-offset-4">
                  Automated Recommendation Engine
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-red-500 animate-pulse"></div>
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">
                  Real-time Insights
                </span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className={`${tableHedBg} text-[10px] uppercase font-semibold text-slate-500`}>
                  <tr>
                    <th className="p-4">Condition</th>
                    <th className="p-4">Anomaly Threshold</th>
                    <th className="p-4">Prescriptive Recommendation</th>
                    <th className="p-4 text-right">Priority Status</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {prescriptiveActions.map((row, i) => (
                    <tr
                      key={i}
                      className={`border-t ${borderThem}  transition-all group`}
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-slate-800 text-blue-500">
                            {row.condition.includes("kWh") ? (
                              <Zap size={14} />
                            ) : row.condition.includes("Pressure") ? (
                              <Waves size={14} />
                            ) : row.condition.includes("Vibration") ? (
                              <Activity size={14} />
                            ) : (
                              <Droplets size={14} />
                            )}
                          </div>
                          <span className="font-semibold group-hover:text-blue-400">
                            {row.condition}
                          </span>
                        </div>
                      </td>
                      <td className="p-4 font-mono text-xs text-slate-500 font-semibold tracking-widest">
                        {row.threshold}
                      </td>
                      <td className="p-4 text-slate-400 italic font-medium underline decoration-slate-700 underline-offset-4">
                        {row.recommendation}
                      </td>
                      <td className="p-4 text-right">
                        <Badge
                          className={
                            row.priority === "Critical"
                              ? "bg-red-500 uppercase text-[8px]"
                              : row.priority === "High"
                              ? "bg-orange-500 uppercase text-[8px]"
                              : "bg-yellow-500 text-black uppercase text-[8px]"
                          }
                        >
                          {row.priority}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* AI Observation Insight */}
          <div className="bg-red-500/5 p-6 rounded-2xl border border-red-500/20 flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-start gap-4">
              <div className="bg-red-500 p-3 text-white rounded-xl shadow-lg shadow-red-500/20">
                <Activity />
              </div>
              <div>
                <p className="text-[10px] font-semibold text-red-500 uppercase tracking-[0.2em] mb-1">
                  Critical Insight Generated
                </p>
                <p className="text-sm font-semibold leading-relaxed italic">
                  "Pump-07 is consuming 35% more energy per m³ than design
                  curve. Systematic inspection of impeller required within 48
                  hours to prevent failure."
                </p>
              </div>
            </div>
            <button className="whitespace-nowrap bg-white text-black font-semibold text-[10px] px-6 py-2 rounded-lg uppercase tracking-widest hover:bg-slate-200 transition-all">
              Schedule Maintenance
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PredictiveAnalytics;
